import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import {
  Form,
  Input,
  Upload,
  Button,
  Card,
  message,
  Spin,
  Collapse,
  Space,
  Row,
  Col,
  Divider,
} from 'antd';
import {
  UploadOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStaticPages from '../useStaticPages';
import {
  ContentSection,
  VipClubSection,
  InstructionSection,
  FindUsSection,
  ServicesListSection,
  OurServicesSection,
  TouristInformationSection,
  MetadataSection,
  HoursSection,
  ExploreSection,
  TransportSection,
  TorinoSection,
  TourismMapSection,
  GiftCardSection,
} from './sections';

const { TextArea } = Input;
const { Panel } = Collapse;

export const StaticPageForm = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [initialData, setInitialData] = useState(null);
  const [availableSections, setAvailableSections] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('IT');
  const allLanguagesDataRef = useRef({});

  // Функція для навігації назад зі збереженням query-параметрів
  const handleBackNavigation = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    // Перевіряємо, чи є дійсно активні фільтри
    const hasActiveFilters =
      searchParams.has('shopping_center_id') ||
      searchParams.has('company_id') ||
      searchParams.has('category_id') ||
      searchParams.has('by_title');

    const queryString = hasActiveFilters ? searchParams.toString() : '';
    const backUrl = queryString
      ? `/website/static_pages?${queryString}`
      : '/website/static_pages';
    navigate(backUrl);
  }, [navigate, location.search]);

  const getAvailableLanguages = () => {
    if (!initialData?.shopping_center) return ['IT', 'EN'];

    const isTorino =
      initialData.shopping_center.title?.toLowerCase().includes('torino') ||
      initialData.shopping_center.name?.toLowerCase().includes('torino') ||
      initialData.shopping_center.id === 'torino';

    return isTorino ? ['IT', 'EN', 'FR'] : ['IT', 'EN'];
  };

  const availableLanguages = getAvailableLanguages();

  // Language selector component
  const LanguageSelector = ({ style = {} }) => (
    <Space style={style}>
      {availableLanguages.map((lang) => (
        <Button
          key={lang}
          type={currentLanguage === lang ? 'primary' : 'default'}
          onClick={() => {
            // Save current language data before switching
            const currentFormValues = form.getFieldsValue();
            if (currentFormValues.content) {
              const currentLangContent =
                currentFormValues.content[currentLanguage.toLowerCase()] || {};
              allLanguagesDataRef.current = {
                ...allLanguagesDataRef.current,
                [currentLanguage.toLowerCase()]: currentLangContent,
              };
            }

            setCurrentLanguage(lang);

            // Restore data for the new language
            setTimeout(() => {
              const langData = allLanguagesDataRef.current[lang.toLowerCase()];
              if (langData) {
                form.setFieldsValue({
                  content: langData,
                });
              }
            }, 100);
          }}
          style={{
            backgroundColor: currentLanguage === lang ? '#002637' : undefined,
            borderColor: currentLanguage === lang ? '#002637' : undefined,
          }}
        >
          {lang}
        </Button>
      ))}
    </Space>
  );

  const { updateStaticPage, fetchStaticPage, uploadFile } = useStaticPages();

  const isEditMode = !!id;

  // Set default sections for new pages
  useEffect(() => {
    if (!isEditMode) {
      setAvailableSections(['metadata_section']);
    }
  }, [isEditMode]);

  // Load static page data for editing
  useEffect(() => {
    if (isEditMode) {
      const loadStaticPage = async () => {
        setLoading(true);
        try {
          const staticPage = await fetchStaticPage(id);
          if (staticPage) {
            setInitialData(staticPage);

            // Prepare content for form
            let formContent = {};
            let isNewStructure = false;
            let firstKey = '';

            if (staticPage.content) {
              // Check if the content is already in language-based structure
              firstKey = Object.keys(staticPage.content)[0];
              isNewStructure = ['it', 'en', 'fr'].includes(firstKey);

              if (isNewStructure) {
                // Content is already in correct format
                formContent = staticPage.content;
              } else {
                // Convert old structure to language-based structure
                availableLanguages.forEach((lang) => {
                  const langKey = lang.toLowerCase();
                  formContent[langKey] = staticPage.content;
                });
              }
            }

            // Ensure all languages have structure
            availableLanguages.forEach((lang) => {
              const langKey = lang.toLowerCase();
              if (!formContent[langKey]) {
                formContent[langKey] = {};
              }

              // Ensure meta section exists
              if (!formContent[langKey].meta) {
                formContent[langKey].meta = {
                  url: '',
                  title: '',
                  description: '',
                };
              }
            });

            form.setFieldsValue({
              title: staticPage.title,
              slug: staticPage.slug,
              content: formContent,
              shoping_center: staticPage.shopping_center?.title,
            });

            // Initialize all languages data
            allLanguagesDataRef.current = formContent;

            // Add default sections if they don't exist
            const defaultSections = ['metadata_section']; // Always include metadata

            // Add page-specific sections
            if (staticPage.slug === 'find-us') {
              defaultSections.push(
                'hero_section',
                'hours_section',
                'explore_section',
                'transport_section',
              );
            } else if (staticPage.slug === 'find-us-visit') {
              defaultSections.push(
                'hero_section',
                'torino_section',
                'tourism_map_section',
              );
            } else if (staticPage.slug === 'explore-village') {
              defaultSections.push('hero_section', 'events_list_section');
            } else if (staticPage.slug === 'home') {
              defaultSections.push(
                'favourites_section',
                'brands_list_section',
              );
            }

            // Get sections from the content
            const sections = new Set(defaultSections);

            if (staticPage.content) {
              if (isNewStructure) {
                // For new structure, get sections from the first language
                const firstLangContent = staticPage.content[firstKey];
                if (firstLangContent) {
                  Object.keys(firstLangContent).forEach((sectionKey) => {
                    if (sectionKey !== 'meta' && sectionKey !== 'links_section') {
                      sections.add(sectionKey);
                    }
                  });
                }
              } else {
                // For old structure, get sections directly
                Object.keys(staticPage.content).forEach((sectionKey) => {
                  if (sectionKey !== 'meta' && sectionKey !== 'links_section') {
                    sections.add(sectionKey);
                  }
                });
              }
            }

            setAvailableSections(Array.from(sections));
          }
        } catch (error) {
          console.error('Error loading static page:', error);
        } finally {
          setLoading(false);
        }
      };

      loadStaticPage();
    }
  }, [id, isEditMode, fetchStaticPage, form]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // Save current language data before submit
      const currentFormValues = form.getFieldsValue();
      if (currentFormValues.content) {
        const currentLangContent =
          currentFormValues.content[currentLanguage.toLowerCase()] || {};
        allLanguagesDataRef.current = {
          ...allLanguagesDataRef.current,
          [currentLanguage.toLowerCase()]: currentLangContent,
        };
      }

      // Convert form data to server format
      const convertToServerFormat = (formData) => {
        const content = {};

        // Initialize content for each language
        availableLanguages.forEach((lang) => {
          const langKey = lang.toLowerCase();
          content[langKey] = {};

          // Get form data for this language from allLanguagesDataRef (includes all language changes)
          const formLangContent = allLanguagesDataRef.current[langKey] || {};
          const initialLangContent = initialData?.content?.[langKey] || {};

          // Get all sections that exist in any language
          const allSections = new Set();
          availableLanguages.forEach((l) => {
            const lKey = l.toLowerCase();
            const lContent = initialData?.content?.[lKey] || {};
            Object.keys(lContent).forEach((sectionKey) =>
              allSections.add(sectionKey),
            );

            // Also add sections from allLanguagesDataRef
            const refContent = allLanguagesDataRef.current[lKey] || {};
            Object.keys(refContent).forEach((sectionKey) =>
              allSections.add(sectionKey),
            );
          });

          // Add sections from form data
          Object.keys(formLangContent).forEach((sectionKey) =>
            allSections.add(sectionKey),
          );

          // Process each section
          allSections.forEach((sectionKey) => {
            if (sectionKey === 'links_section') {
              return;
            }

            const formSection = formLangContent[sectionKey] || {};
            const initialSection = initialLangContent[sectionKey] || {};

            // Deep merge sections
            content[langKey][sectionKey] = {
              ...initialSection,
              ...formSection,
            };

            // Special handling for transport_section
            if (sectionKey === 'transport_section' && formSection.selects) {
              content[langKey][sectionKey].selects = formSection.selects
                .map((select) => ({
                  icon: select.icon || '',
                  icon_active: select.icon_active || '',
                  title: select.title || '',
                  tabs: (select.tabs || []).map((tab) => ({
                    title: tab.title || '',
                    button: tab.button || '',
                    description: tab.description || '',
                  })),
                }))
                .filter(
                  (select) => select.title || select.icon || select.icon_active,
                );
            }
          });

          // Always include meta section
          if (!content[langKey].meta) {
            content[langKey].meta = {
              url: formLangContent.meta?.url || '',
              title: formLangContent.meta?.title || '',
              description: formLangContent.meta?.description || '',
            };
          }
        });

        return {
          ...formData,
          content,
        };
      };

      const serverData = convertToServerFormat(values);
      const updateResult = await updateStaticPage(id, serverData);
      
      if (updateResult) {
        const freshData = await fetchStaticPage(id);
        if (freshData) {
          setInitialData(freshData);
          
          const currentFormValues = form.getFieldsValue();
          if (currentFormValues.content) {
            const currentLangContent =
              currentFormValues.content[currentLanguage.toLowerCase()] || {};
            allLanguagesDataRef.current = {
              ...allLanguagesDataRef.current,
              [currentLanguage.toLowerCase()]: currentLangContent,
            };
          }
          
          if (freshData.content) {
            const newAllLanguagesData = {};
            availableLanguages.forEach((lang) => {
              const langKey = lang.toLowerCase();
    
              const existingChanges = allLanguagesDataRef.current[langKey];
              newAllLanguagesData[langKey] = existingChanges || freshData.content[langKey] || {};
            });
            allLanguagesDataRef.current = newAllLanguagesData;
          }
        }
      }
    } catch (error) {
      console.error('Error updating static page:', error);
      message.error(t('Errore durante l\'aggiornamento della pagina statica'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = useCallback(({ fileList: newFileList }) => {
    setFileList(newFileList);
  }, []);

  const uploadProps = {
    fileList,
    onChange: handleFileChange,
    beforeUpload: () => false, // Prevent auto upload
    accept: 'image/*',
  };

  const handleImageUpload = useCallback(
    async (file, fieldPath) => {
      setUploadingImage(true);
      try {
        const result = await uploadFile(file);
        if (result?.url) {
          // Update the form field with the uploaded image URL
          const currentValues = form.getFieldsValue();
          const pathArray = fieldPath.split('.');
          let current = currentValues;

          // Navigate to the nested field
          for (let i = 0; i < pathArray.length - 1; i++) {
            if (!current[pathArray[i]]) {
              current[pathArray[i]] = {};
            }
            current = current[pathArray[i]];
          }

          // Set the image URL
          current[pathArray[pathArray.length - 1]] = result.url;
          form.setFieldsValue(currentValues);

          // message.success(t('Immagine caricata con successo'));
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        message.error(t('Errore durante il caricamento dell\'immagine'));
      } finally {
        setUploadingImage(false);
      }
    },
    [uploadFile, form, t],
  );

  const handleImageUploadForSection = useCallback(
    async (file, sectionKey, selectIndex, iconType = 'icon') => {
      setUploadingImage(true);
      try {
        const result = await uploadFile(file);
        if (result?.url) {
          const currentValues = form.getFieldsValue();
          const langKey = currentLanguage.toLowerCase();

          if (!currentValues.content) {
            currentValues.content = {};
          }
          if (!currentValues.content[langKey]) {
            currentValues.content[langKey] = {};
          }
          if (!currentValues.content[langKey][sectionKey]) {
            currentValues.content[langKey][sectionKey] = {};
          }

          // For transport_section, we need to handle the selects array
          if (typeof selectIndex === 'number') {
            const selectsPath = ['content', langKey, sectionKey, 'selects'];
            const selects = form.getFieldValue(selectsPath) || [];

            // Update the specific select's icon or icon_active
            if (selects[selectIndex]) {
              const newSelects = [...selects];
              newSelects[selectIndex] = {
                ...newSelects[selectIndex],
                [iconType]: result.url,
              };

              // Update form values
              const updatedValues = {
                content: {
                  ...currentValues.content,
                  [langKey]: {
                    ...currentValues.content[langKey],
                    [sectionKey]: {
                      ...currentValues.content[langKey][sectionKey],
                      selects: newSelects,
                    },
                  },
                },
              };
              form.setFieldsValue(updatedValues);

              // Update allLanguagesDataRef
              if (!allLanguagesDataRef.current[langKey]) {
                allLanguagesDataRef.current[langKey] = {};
              }
              if (!allLanguagesDataRef.current[langKey][sectionKey]) {
                allLanguagesDataRef.current[langKey][sectionKey] = {};
              }
              if (!allLanguagesDataRef.current[langKey][sectionKey].selects) {
                allLanguagesDataRef.current[langKey][sectionKey].selects = [];
              }
              allLanguagesDataRef.current[langKey][sectionKey].selects =
                newSelects;
            }
          } else {
            // For other sections, update normally
            const updatedValues = {
              content: {
                ...currentValues.content,
                [langKey]: {
                  ...currentValues.content[langKey],
                  [sectionKey]: {
                    ...currentValues.content[langKey]?.[sectionKey],
                    image: result.url,
                  },
                },
              },
            };
            form.setFieldsValue(updatedValues);

            // Update allLanguagesDataRef
            if (!allLanguagesDataRef.current[langKey]) {
              allLanguagesDataRef.current[langKey] = {};
            }
            if (!allLanguagesDataRef.current[langKey][sectionKey]) {
              allLanguagesDataRef.current[langKey][sectionKey] = {};
            }
            allLanguagesDataRef.current[langKey][sectionKey].image = result.url;
          }

          // Force form to re-render
          form.validateFields();
        } else {
          message.error(t('Errore: URL non trovato nella risposta del server'));
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        message.error(t('Errore durante il caricamento dell\'immagine'));
      } finally {
        setUploadingImage(false);
      }
    },
    [uploadFile, form, t, currentLanguage],
  );

  const handleImageUploadForItem = useCallback(
    async (file, sectionKey, itemIndex, imageField = 'image') => {
      setUploadingImage(true);
      try {
        const result = await uploadFile(file);
        if (result?.url) {
          const currentValues = form.getFieldsValue();
          const langKey = currentLanguage.toLowerCase();

          if (!currentValues.content) {
            currentValues.content = {};
          }
          if (!currentValues.content[langKey]) {
            currentValues.content[langKey] = {};
          }
          if (!currentValues.content[langKey][sectionKey]) {
            currentValues.content[langKey][sectionKey] = {};
          }
          if (!currentValues.content[langKey][sectionKey].items) {
            currentValues.content[langKey][sectionKey].items = [];
          }
          if (!currentValues.content[langKey][sectionKey].items[itemIndex]) {
            currentValues.content[langKey][sectionKey].items[itemIndex] = {};
          }

          // Update form values
          const updatedValues = {
            content: {
              ...currentValues.content,
              [langKey]: {
                ...currentValues.content[langKey],
                [sectionKey]: {
                  ...currentValues.content[langKey][sectionKey],
                  items: currentValues.content[langKey][sectionKey].items.map(
                    (item, idx) =>
                      idx === itemIndex ? { ...item, [imageField]: result.url } : item,
                  ),
                },
              },
            },
          };
          form.setFieldsValue(updatedValues);

          // Update allLanguagesDataRef
          if (!allLanguagesDataRef.current[langKey]) {
            allLanguagesDataRef.current[langKey] = {};
          }
          if (!allLanguagesDataRef.current[langKey][sectionKey]) {
            allLanguagesDataRef.current[langKey][sectionKey] = {};
          }
          if (!allLanguagesDataRef.current[langKey][sectionKey].items) {
            allLanguagesDataRef.current[langKey][sectionKey].items = [];
          }
          if (
            !allLanguagesDataRef.current[langKey][sectionKey].items[itemIndex]
          ) {
            allLanguagesDataRef.current[langKey][sectionKey].items[itemIndex] =
              {};
          }
          allLanguagesDataRef.current[langKey][sectionKey].items[
            itemIndex
          ][imageField] = result.url;

          // Force form to re-render
          form.validateFields();
        } else {
          message.error(t('Errore: URL non trovato nella risposta del server'));
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        message.error(t('Errore durante il caricamento dell\'immagine'));
      } finally {
        setUploadingImage(false);
      }
    },
    [uploadFile, form, t, currentLanguage],
  );

  const handleImageUploadForItemIcon = useCallback(
    async (file, sectionKey, itemIndex) => {
      setUploadingImage(true);
      try {
        const result = await uploadFile(file);
        if (result?.url) {
          // Update the form field with the uploaded image URL
          const currentValues = form.getFieldsValue();
          const langKey = currentLanguage.toLowerCase();

          if (!currentValues.content) {
            currentValues.content = {};
          }
          if (!currentValues.content[langKey]) {
            currentValues.content[langKey] = {};
          }
          if (!currentValues.content[langKey][sectionKey]) {
            currentValues.content[langKey][sectionKey] = {};
          }
          if (!currentValues.content[langKey][sectionKey].items) {
            currentValues.content[langKey][sectionKey].items = [];
          }
          if (!currentValues.content[langKey][sectionKey].items[itemIndex]) {
            currentValues.content[langKey][sectionKey].items[itemIndex] = {};
          }
          currentValues.content[langKey][sectionKey].items[itemIndex].icon =
            result.url;
          form.setFieldsValue(currentValues);

          // message.success(t('Immagine caricata con successo'));
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        message.error(t('Errore durante il caricamento dell\'immagine'));
      } finally {
        setUploadingImage(false);
      }
    },
    [uploadFile, form, t, currentLanguage],
  );

  const handleImageUploadForSliderImage = useCallback(
    async (file, sectionKey, imageIndex) => {
      setUploadingImage(true);
      try {
        const result = await uploadFile(file);
        if (result?.url) {
          // Update the form field with the uploaded image URL
          const currentValues = form.getFieldsValue();
          const langKey = currentLanguage.toLowerCase();

          if (!currentValues.content) {
            currentValues.content = {};
          }
          if (!currentValues.content[langKey]) {
            currentValues.content[langKey] = {};
          }
          if (!currentValues.content[langKey][sectionKey]) {
            currentValues.content[langKey][sectionKey] = {};
          }
          if (!currentValues.content[langKey][sectionKey].slider_images) {
            currentValues.content[langKey][sectionKey].slider_images = [];
          }
          if (
            !currentValues.content[langKey][sectionKey].slider_images[
              imageIndex
            ]
          ) {
            currentValues.content[langKey][sectionKey].slider_images[
              imageIndex
            ] = {};
          }

          // Update form values
          const updatedValues = {
            content: {
              ...currentValues.content,
              [langKey]: {
                ...currentValues.content[langKey],
                [sectionKey]: {
                  ...currentValues.content[langKey][sectionKey],
                  slider_images: currentValues.content[langKey][
                    sectionKey
                  ].slider_images.map((item, idx) =>
                    idx === imageIndex ? { ...item, image: result.url } : item,
                  ),
                },
              },
            },
          };
          form.setFieldsValue(updatedValues);

          // Update allLanguagesDataRef
          if (!allLanguagesDataRef.current[langKey]) {
            allLanguagesDataRef.current[langKey] = {};
          }
          if (!allLanguagesDataRef.current[langKey][sectionKey]) {
            allLanguagesDataRef.current[langKey][sectionKey] = {};
          }
          if (!allLanguagesDataRef.current[langKey][sectionKey].slider_images) {
            allLanguagesDataRef.current[langKey][sectionKey].slider_images = [];
          }
          if (
            !allLanguagesDataRef.current[langKey][sectionKey].slider_images[
              imageIndex
            ]
          ) {
            allLanguagesDataRef.current[langKey][sectionKey].slider_images[
              imageIndex
            ] = {};
          }
          allLanguagesDataRef.current[langKey][sectionKey].slider_images[
            imageIndex
          ].image = result.url;

          // Force form to re-render
          form.validateFields();
        } else {
          message.error(t('Errore: URL non trovato nella risposta del server'));
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        message.error(t('Errore durante il caricamento dell\'immagine'));
      } finally {
        setUploadingImage(false);
      }
    },
    [uploadFile, form, t, currentLanguage],
  );

  const getSectionTitle = (sectionKey) => {
    const sectionTitles = {
      hero_section: t('Sezione Hero'),
      content_section: t('Sezione Contenuto'),
      items_section: t('Sezione Elementi'),
      vip_club_section: t('Sezione VIP Club'),
      instruction_section: t('Sezione Istruzioni'),
      find_us_section: t('Sezione Trovaci'),
      services_list_section: t('Sezione Lista Servizi'),
      our_services_section: t('Sezione I Nostri Servizi'),
      tourist_information_section: t('Sezione Informazioni Turistiche'),
      download_app_section: t('Sezione Download App'),
      metadata_section: t('Sezione Metadati'),
      meta: t('Sezione Metadati'),
      hours_section: t('Sezione Orari'),
      explore_section: t('Sezione Esplora'),
      transport_section: t('Sezione Trasporto'),
      torino_section: t('Sezione Torino'),
      tourism_map_section: t('Sezione Mappa Turistica'),
      events_list_section: t('Sezione Lista Eventi'),
      shops_section: t('Sezione Negozi'),
      hotels_section: t('Sezione Hotel'),
      shopping_section: t('Sezione Shopping'),
      gift_card_section: t('Sezione Gift Card'),
      offers_list_section: t('Sezione Lista Offerte'),
      brands_list_section: t('Sezione Lista Marchi'),
      find_your_brand_section: t('Sezione Trova il tuo Marchio'),
      information_section: t('Sezione Informazioni'),
      favourites_section: t('Sezione Preferiti'),
      restaurants_list_section: t('Sezione Lista Ristoranti'),
      form_section: t('Sezione Form'),
      tourist_itineraries_section: t('Sezione Itinerari Turistici'),
      partnerships_section: t('Sezione Partnership'),
    };

    return sectionTitles[sectionKey] || sectionKey;
  };

  const renderSection = (sectionKey) => {
    const sectionTitle = getSectionTitle(sectionKey);

    // Get section data for current language
    const langKey = currentLanguage.toLowerCase();
    let sectionData;

    if (sectionKey === 'metadata_section' || sectionKey === 'meta') {
      // For metadata section, get data from form content for current language
      const formMetaData = form.getFieldValue(['content', langKey, 'meta']);
      const initialMetaData = initialData?.content?.[langKey]?.meta;
      sectionData = formMetaData || initialMetaData || {};
    } else {
      // For other sections, get data from form content for current language
      const formSectionData = form.getFieldValue([
        'content',
        langKey,
        sectionKey,
      ]);
      const initialSectionData = initialData?.content?.[langKey]?.[sectionKey];

      // If no data exists for current language, try to get structure from other languages
      if (!formSectionData && !initialSectionData) {
        // Try to get structure from other available languages
        const otherLangData = availableLanguages
          .filter((lang) => lang !== currentLanguage)
          .map(
            (lang) => initialData?.content?.[lang.toLowerCase()]?.[sectionKey],
          )
          .find((data) => data);

        if (otherLangData) {
          // Use the structure but with empty values
          sectionData = Object.keys(otherLangData).reduce((acc, key) => {
            if (Array.isArray(otherLangData[key])) {
              acc[key] = [];
            } else if (
              typeof otherLangData[key] === 'object' &&
              otherLangData[key] !== null
            ) {
              acc[key] = {};
            } else {
              acc[key] = '';
            }
            return acc;
          }, {});
        } else {
          // Create default structure for common sections
          const defaultStructure = {
            vip_club_section: { items: [] },
            instruction_section: { items: [] },
            our_services_section: { items: [] },
            find_us_section: { slider_images: [] },
            hours_section: {
              working_hours_section: { title: '', description: '' },
              extraordinary_hours_section: { title: '', description: '' },
            },
            explore_section: {
              title: '',
              description: '',
              button: '',
              image: '',
            },
            transport_section: { selects: [] },
            torino_section: { title: '', description: '', image: '' },
            tourism_map_section: { title: '', description: '', image: '' },
            hero_section: { title: '', description: '', button: '', image: '' },
            information_section: {
              name: '',
              title: '',
              description: '',
              button: '',
              image: '',
            },
            favourites_section: { title: '' },
            brands_list_section: { title: '', button: '' },
          };
          sectionData = defaultStructure[sectionKey] || {};
        }
      } else {
        sectionData = formSectionData || initialSectionData || {};
      }
    }

    const commonProps = {
      sectionKey,
      sectionData,
      uploadingImage,
      uploadProps,
      currentLanguage,
      form,
      pageSlug: initialData?.slug,
    };

    let sectionContent;
    if (sectionKey === 'vip_club_section') {
      sectionContent = (
        <VipClubSection
          {...commonProps}
          handleImageUploadForSection={handleImageUploadForSection}
          handleImageUploadForItem={handleImageUploadForItem}
          handleImageUploadForItemIcon={handleImageUploadForItemIcon}
        />
      );
    } else if (sectionKey === 'instruction_section') {
      sectionContent = (
        <InstructionSection
          {...commonProps}
          handleImageUploadForSection={handleImageUploadForSection}
          handleImageUploadForItemIcon={handleImageUploadForItemIcon}
        />
      );
    } else if (sectionKey === 'find_us_section') {
      sectionContent = (
        <FindUsSection
          {...commonProps}
          handleImageUploadForSliderImage={handleImageUploadForSliderImage}
        />
      );
    } else if (sectionKey === 'services_list_section') {
      sectionContent = <ServicesListSection {...commonProps} />;
    } else if (sectionKey === 'tourist_information_section') {
      sectionContent = (
        <TouristInformationSection
          {...commonProps}
          handleImageUploadForSection={handleImageUploadForSection}
        />
      );
    } else if (sectionKey === 'our_services_section') {
      sectionContent = (
        <OurServicesSection
          {...commonProps}
          handleImageUploadForItem={handleImageUploadForItem}
        />
      );
    } else if (sectionKey === 'metadata_section' || sectionKey === 'meta') {
      sectionContent = <MetadataSection {...commonProps} />;
    } else if (sectionKey === 'hours_section') {
      sectionContent = <HoursSection {...commonProps} />;
    } else if (sectionKey === 'explore_section') {
      sectionContent = (
        <ExploreSection
          {...commonProps}
          handleImageUploadForSection={handleImageUploadForSection}
        />
      );
    } else if (sectionKey === 'transport_section') {
      sectionContent = (
        <TransportSection
          {...commonProps}
          handleImageUploadForSection={handleImageUploadForSection}
        />
      );
    } else if (sectionKey === 'torino_section') {
      sectionContent = (
        <TorinoSection
          {...commonProps}
          handleImageUploadForSection={handleImageUploadForSection}
        />
      );
    } else if (sectionKey === 'tourism_map_section') {
      sectionContent = (
        <TourismMapSection
          {...commonProps}
          handleImageUploadForSection={handleImageUploadForSection}
        />
      );
    } else if (sectionKey === 'gift_card_section') {
      sectionContent = (
        <GiftCardSection
          {...commonProps}
          handleImageUploadForSection={handleImageUploadForSection}
        />
      );
    } else if (sectionKey === 'favourites_section') {
      sectionContent = (
        <ContentSection
          {...commonProps}
          handleImageUploadForSection={handleImageUploadForSection}
        />
      );
    } else if (sectionKey === 'brands_list_section') {
      sectionContent = (
        <ContentSection
          {...commonProps}
          handleImageUploadForSection={handleImageUploadForSection}
        />
      );
    } else {
      sectionContent = (
        <ContentSection
          {...commonProps}
          handleImageUploadForSection={handleImageUploadForSection}
        />
      );
    }

    return (
      <Panel header={sectionTitle} key={sectionKey}>
        {sectionContent}
      </Panel>
    );
  };

  if (loading && isEditMode) {
    return (
      <Card title={t('Caricamento...')}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size='large' />
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={t('Modifica Pagina Statica')}
      extra={
        <Button icon={<ArrowLeftOutlined />} onClick={handleBackNavigation}>
          {t('Indietro')}
        </Button>
      }
    >
      <Form
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
        onValuesChange={(changedValues, allValues) => {
          if (changedValues.content) {
            // Extract the actual content for the current language
            const currentLangContent =
              allValues.content[currentLanguage.toLowerCase()] || {};
            allLanguagesDataRef.current = {
              ...allLanguagesDataRef.current,
              [currentLanguage.toLowerCase()]: currentLangContent,
            };
          }
        }}
        initialValues={{
          content: {},
        }}
      >
        <Row>
          <Col span={6}>
            <Form.Item name='title' label={t('Titolo')}>
              <Input
                placeholder={t('Inserisci il titolo')}
                style={{ width: 340 }}
                disabled
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name='slug' label={t('Slug')}>
              <Input
                placeholder={t('Inserisci lo slug')}
                style={{ width: 340 }}
                disabled
              />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item name='shoping_center' label={t('Outlet Village')}>
              <Input
                placeholder={t('Inserisci lo shoping center')}
                style={{ width: 340 }}
                disabled
              />
            </Form.Item>
          </Col>
        </Row>
        <Divider style={{ margin: '0px 0 24px', borderColor: '#f0f0f0' }} />
        <Form.Item label={t('Contenuto')}>
          <div style={{ marginBottom: 16 }}>
            <LanguageSelector />
          </div>
          <Collapse>
            {availableSections.map((sectionKey) => {
              return renderSection(sectionKey);
            })}
          </Collapse>
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type='primary'
              htmlType='submit'
              loading={loading}
              icon={<SaveOutlined />}
              style={{ backgroundColor: '#002637', borderColor: '#002637' }}
            >
              {t('Aggiorna')}
            </Button>
            <Button onClick={handleBackNavigation}>{t('Annulla')}</Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
});
